import styled from "styled-components"

import {
  breakpoint,
  cardBoxShadow,
  colors,
  colorScale,
  Popover,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"
import { useMatchRoute } from "@tanstack/react-router"
import { TTPlaceholder, useTranslate } from "src/domains/i18n/use-translate"

import { isSameSession } from "./selected-session-storage"
import { TFetchSessionsResponse } from "./session-types"
import { useSession } from "./use-session"

type TSession = TFetchSessionsResponse["sessions"][number]

// Routes the dropdown is shown on; it is hidden everywhere else. `fuzzy`
// lets a route also cover its descendants (e.g. `/mfa` also matches `/mfa/setup`
// and `/mfa/validate`).
const VISIBLE_ON_ROUTES = [{ to: "/mfa", fuzzy: true }] as const

export function SessionSelectDropdown() {
  const { tPlaceholder } = useTranslate()
  const { sessions, selectedSession, setSelectedSession } = useSession()
  const matchRoute = useMatchRoute()

  const isVisibleRoute = VISIBLE_ON_ROUTES.some((route) =>
    Boolean(matchRoute(route))
  )

  if (!isVisibleRoute || sessions.length < 2) {
    return null
  }

  if (selectedSession === undefined) {
    return null
  }

  return (
    <Floating>
      <Popover
        placement="bottom-start"
        trigger={
          <TriggerButton type="button">
            <Text variant="bodyS">
              {accountLabel(selectedSession, tPlaceholder)}
            </Text>
            <Caret />
          </TriggerButton>
        }
      >
        {({ closeMenu }) => (
          <Menu>
            {sessions.map((session) => (
              <MenuItem
                key={`${session.user_id}:${session.is_admin}`}
                type="button"
                $active={isSameSession(session, selectedSession)}
                onClick={() => {
                  setSelectedSession(session)
                  closeMenu()
                }}
              >
                <Text variant="bodyS" color="secondary">
                  {accountLabel(session, tPlaceholder)}
                </Text>
                <Text variant="bodyS">{session.email}</Text>
              </MenuItem>
            ))}
          </Menu>
        )}
      </Popover>
    </Floating>
  )
}

function accountLabel(session: TSession, tPlaceholder: TTPlaceholder) {
  return session.is_admin
    ? tPlaceholder("Admin account")
    : tPlaceholder("Personal account")
}

const Floating = styled.div`
  position: fixed;
  top: ${spacing.M};
  left: ${spacing.M};
  z-index: 2;

  @media (${breakpoint.mediumUp}) {
    top: ${spacing.L};
    left: ${spacing.L};
  }
`

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.XS2};
  padding: ${spacing.XS2} ${spacing.S};
  border: 1px solid ${colors.divider};
  border-radius: 999px;
  background: ${colorScale.white};
  text-align: inherit;
  color: inherit;
  cursor: pointer;
  transition: box-shadow 0.15s ease-out;
  ${cardBoxShadow};

  &:hover {
    box-shadow:
      0px 0px 4px 0px rgba(18, 31, 65, 0.08),
      0px 8px 16px 0px rgba(18, 31, 65, 0.05);
  }

  &:focus-visible {
    outline: 2px solid ${colors.textPrimary};
    outline-offset: 2px;
  }
`

const Caret = styled.span`
  width: 6px;
  height: 6px;
  margin-top: -3px;
  border-right: 1.5px solid ${colors.textSecondary};
  border-bottom: 1.5px solid ${colors.textSecondary};
  transform: rotate(45deg);
`

const Menu = styled.div`
  display: grid;
  gap: ${spacing.XS2};
  padding: ${spacing.XS};
  min-width: 24ch;
  background: ${colorScale.white};
  border: 1px solid ${colors.divider};
  border-radius: 16px;
  ${cardBoxShadow};
`

const MenuItem = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  padding: ${spacing.S};
  border: none;
  border-radius: 12px;
  background: ${(props) => (props.$active ? colors.neutral : "none")};
  text-align: inherit;
  color: inherit;
  cursor: pointer;

  &:hover {
    background: ${colors.neutral};
  }

  &:focus-visible {
    outline: 2px solid ${colors.textPrimary};
    outline-offset: -2px;
  }
`
